import { ResourceOutput, ResourceType } from '@mxa-argos/core/graphql/graphql';
import { buildSearchResourcesQuery } from '@mxa-argos/core/queries/search.queries';
import { useDataProvider } from '@refinedev/core';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type SearchResult = {
  id: string;
  type: ResourceType;
  primary: string;
  secondary?: string;
  tertiary?: string;
  quaternary?: string;
};

export const routeByResourceType: Partial<Record<ResourceType, string>> = {
  [ResourceType.DEVICE]: '/devices',
  [ResourceType.ORDER]: '/orders',
  [ResourceType.PERSON]: '/customers',
  [ResourceType.SIM]: '/sims',
  [ResourceType.SUBSCRIPTION]: '/subscriptions',
  [ResourceType.PORTABILITY]: '/portabilities',
  [ResourceType.CHANGE]: '/changes',
};

const MIN_SEARCH_QUERY_LENGTH = 3;

export function useResourceSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const dataProvider = useDataProvider();
  const provider = dataProvider();

  const searchResources = useCallback(
    async (value: string) => {
      if (!value.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const { operation, gqlQuery, variables } = buildSearchResourcesQuery(value, [
          ResourceType.DEVICE,
          ResourceType.ORDER,
          ResourceType.PERSON,
          ResourceType.SIM,
          ResourceType.SUBSCRIPTION,
          ResourceType.PORTABILITY,
          ResourceType.CHANGE,
        ]);
        const { data } = await provider.getList<ResourceOutput>({
          resource: 'resources',
          meta: { gqlQuery, operation, variables },
        });

        const nextResults: SearchResult[] = data.map((item) => ({
          id: item.id,
          type: item.type,
          primary: item.primaryText,
          secondary: item.secondaryText ?? undefined,
          tertiary: item.tertiaryText ?? undefined,
          quaternary: item.quaternaryText ?? undefined,
        }));
        setResults(nextResults);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [provider],
  );

  const debouncedSearch = useMemo(
    () => debounce((value: string) => searchResources(value), 300),
    [searchResources],
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const handleValueChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim().length < MIN_SEARCH_QUERY_LENGTH) {
      debouncedSearch.cancel();
      setResults([]);
      return;
    }
    void debouncedSearch(value);
  };

  const reset = () => {
    debouncedSearch.cancel();
    setSearchQuery('');
    setResults([]);
  };

  const RESOURCE_TYPE_PRIORITY: ResourceType[] = [
    ResourceType.SUBSCRIPTION,
    ResourceType.PERSON,
    ResourceType.DEVICE,
    ResourceType.SIM,
    ResourceType.ORDER,
    ResourceType.PORTABILITY,
    ResourceType.CHANGE,
  ];

  const groupedResults = useMemo(() => {
    return Object.groupBy(results, (item) => item.type);
  }, [results]);

  const groupEntries = (Object.entries(groupedResults) as [ResourceType, SearchResult[]][]).sort(
    ([typeA], [typeB]) => {
      return RESOURCE_TYPE_PRIORITY.indexOf(typeA) - RESOURCE_TYPE_PRIORITY.indexOf(typeB);
    },
  );

  const trimmedQueryLength = searchQuery.trim().length;
  const isQueryTooShort = trimmedQueryLength > 0 && trimmedQueryLength < MIN_SEARCH_QUERY_LENGTH;

  return {
    searchQuery,
    loading,
    results,
    groupEntries,
    hasResults: results.length > 0,
    isQueryTooShort,
    handleValueChange,
    reset,
  };
}
